/*
 * Single shared place to normalize the DISPLAY case of Size/Color variant
 * option values coming from the DB (values were bulk-normalized to lowercase
 * there; this only fixes how they render, never the underlying data).
 *
 * Pure helpers live on window.BkrmVariantFormat so they are unit-testable,
 * same pattern as window.BkrmBidOffer / window.BkrmConfigurator.
 *
 * Usage: mark any element whose text is a Size/Color value coming straight
 * from the DB with `data-variant-size` / `data-variant-color` in Liquid.
 * This script formats them on load AND keeps formatting them forever after,
 * via a MutationObserver — so any component that later rewrites the text of
 * an already-marked element (variant switch, AJAX cart refresh, popups...)
 * gets the fix for free, with no per-component wiring needed.
 */
(function () {
  "use strict";

  var SIZE_SELECTOR = "[data-variant-size]";
  var COLOR_SELECTOR = "[data-variant-color]";

  // Uppercase — sizes ("s", "xl", "51") always read as capital letters.
  function formatSize(value) {
    if (value == null) return value;
    return String(value).toUpperCase();
  }

  // Capitalize each word — colors can be multi-word, separated by spaces or
  // hyphens (e.g. "carbon-white-red", "brown light blue").
  function formatColor(value) {
    if (value == null) return value;
    return String(value).replace(/[^\s-]+/g, function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
  }

  function applyTo(el, formatter) {
    if (!el || !el.isConnected) return;
    var current = el.tagName === "OPTION" ? el.textContent : el.textContent;
    var next = formatter(current);
    if (next !== current) el.textContent = next;
  }

  function formatWithin(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches(SIZE_SELECTOR)) applyTo(root, formatSize);
    if (root.matches(COLOR_SELECTOR)) applyTo(root, formatColor);
    root.querySelectorAll(SIZE_SELECTOR).forEach(function (el) {
      applyTo(el, formatSize);
    });
    root.querySelectorAll(COLOR_SELECTOR).forEach(function (el) {
      applyTo(el, formatColor);
    });
  }

  function scanAll() {
    formatWithin(document.body);
  }

  function startObserving() {
    if (!document.body) return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === "childList") {
          // Whole marked subtrees inserted (AJAX cart/section re-render, popups, ...).
          mutation.addedNodes.forEach(function (node) {
            formatWithin(node);
          });
          // `el.textContent = value` (product-configurator.js, etc.) removes the old
          // Text node and appends a new one — that's a childList mutation on the
          // marked element itself, not a characterData mutation. Re-check the host.
          var target = mutation.target;
          if (target && target.nodeType === 1) {
            var sizeHost = target.closest(SIZE_SELECTOR);
            var colorHost = target.closest(COLOR_SELECTOR);
            if (sizeHost) applyTo(sizeHost, formatSize);
            if (colorHost) applyTo(colorHost, formatColor);
          }
        } else if (mutation.type === "characterData") {
          var parent = mutation.target.parentElement;
          var charSizeHost = parent && parent.closest(SIZE_SELECTOR);
          var charColorHost = parent && parent.closest(COLOR_SELECTOR);
          if (charSizeHost) applyTo(charSizeHost, formatSize);
          if (charColorHost) applyTo(charColorHost, formatColor);
        }
      });
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return observer;
  }

  function init() {
    scanAll();
    startObserving();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.BkrmVariantFormat = {
    formatSize: formatSize,
    formatColor: formatColor,
    scanAll: scanAll,
  };
})();
